/** Browser-native PDF output: no remote service, runtime asset or persistent cache. */
export const PRINT_PAGE_HEIGHT = 650;
export const fullNightPrintCss = `
.full-night-export { --track-label-width:280px; width:1080px; font:14px Arial,sans-serif; background:white; color:#172c40; }
.full-night-export * { box-sizing:border-box; print-color-adjust:exact; -webkit-print-color-adjust:exact; }
.full-night-export .signal-track-row { display:grid; grid-template-columns:280px 1fr; min-height:92px; height:auto; break-inside:avoid; border-bottom:1px solid #abb8c2; }
.full-night-export .signal-track-label { padding:8px; overflow:visible; }
.full-night-export .signal-track-label strong { white-space:normal; overflow-wrap:anywhere; font-size:14px; }
.full-night-export .signal-track-plot { min-width:0; min-height:84px; }
.full-night-export .signal-row-canvas { width:100%; height:84px; }
.full-night-export .channel-value, .full-night-export button.channel-cog, .full-night-export .shared-track-cursor { display:none!important; }
.full-night-export .print-unit, .full-night-export .print-legend { display:flex; flex-wrap:wrap; gap:4px; font:12px Arial,sans-serif; padding:3px 0; }
.full-night-export .signal-time-axis { margin-left:0; padding-left:280px; display:flex; justify-content:space-between; padding:8px 0 8px 280px; }
.full-night-export .sleep-stage-plot { width:100%; height:108px; }
.full-night-export .sleep-stage-plot svg { width:100%; height:84px; }
.full-night-export .presentation-legend, .full-night-export .sleep-stage-cues { font-size:12px; }
.full-night-export .operational-hypnogram-card { margin:0; padding:8px; }
.full-night-export .hypnogram-result { margin:0; }
.print-page { width:1080px; break-after:page; }
.print-page:last-child { break-after:auto; }
.print-heading { font:14px Arial,sans-serif; margin:0 0 6px; overflow-wrap:anywhere; }
@media print { .full-night-export { width:100%; } .print-page { width:100%; } }
`;

/** Clone every mounted row, including offscreen canvases, without moving the live viewer. */
export function printableClone(node: HTMLElement): HTMLElement {
  const clone = node.cloneNode(true) as HTMLElement;
  const originals = node.querySelectorAll("canvas");
  clone.querySelectorAll("canvas").forEach((canvas, index) => {
    const image = document.createElement("img");
    image.src = originals[index].toDataURL("image/png");
    image.className = canvas.className;
    image.style.cssText = canvas.style.cssText;
    image.style.width = "100%";
    image.style.height = `${originals[index].getBoundingClientRect().height}px`;
    image.alt = "Full-night channel plot";
    canvas.replaceWith(image);
  });
  clone.querySelectorAll("button.channel-cog, .channel-value, .shared-track-cursor, .hypnogram-cursor, details").forEach(n=>n.remove());
  return clone;
}

export async function openFullNightPrint(host: HTMLElement, title: string, signal: AbortSignal, authorize: () => Promise<void>): Promise<() => void> {
  signal.throwIfAborted();
  const frame=document.createElement("iframe");
  frame.title="Full-night PDF print preview";
  frame.style.cssText="position:fixed;left:-20000px;top:0;width:1120px;height:800px;border:0";
  document.body.append(frame);
  let timer:ReturnType<typeof setTimeout>|undefined;
  const cleanup=()=>{clearTimeout(timer);signal.removeEventListener("abort",cleanup);frame.remove();};
  signal.addEventListener("abort",cleanup,{once:true});
  try {
    const doc=frame.contentDocument!;
    doc.open();doc.write('<!doctype html><html><head></head><body class="full-night-export"></body></html>');doc.close();
    doc.title=title;
    // Same-origin installed app styles only; never an export-specific CDN.
    const styles=Array.from(document.querySelectorAll('style,link[rel="stylesheet"]')).map(style=>{
      const copy=style.cloneNode(true) as HTMLElement;
      const ready=style instanceof HTMLLinkElement?new Promise<void>((resolve,reject)=>{
        copy.onload=()=>resolve();copy.onerror=()=>reject(Error("Print stylesheet unavailable. Try again."));
      }):Promise.resolve();
      doc.head.append(copy);return ready;
    });
    const style=doc.createElement("style");style.textContent="@page { size: A4 landscape; margin: 10mm; } html,body{margin:0!important;background:white!important;color:#172c40!important;}"+fullNightPrintCss;doc.head.append(style);
    const axis=host.querySelector<HTMLElement>(".signal-time-axis")!;
    let page:HTMLElement, used=PRINT_PAGE_HEIGHT;
    const rows=Array.from(host.querySelectorAll<HTMLElement>("[data-print-row]"));
    rows.forEach((row)=>{
      const height=Math.ceil(row.getBoundingClientRect().height);
      if(height>PRINT_PAGE_HEIGHT-80)throw Error("A channel is too tall for a readable page. Shorten its display label and try again.");
      if(used+height>PRINT_PAGE_HEIGHT){
        page=doc.createElement("section");page.className="print-page";
        const heading=doc.createElement("h1");heading.className="print-heading";heading.textContent=`${title} · page ${doc.body.children.length+1}`;
        page.append(heading,axis.cloneNode(true));doc.body.append(page);used=80;
      }
      page.append(printableClone(row));used+=height;
    });
    if(!rows.length)throw Error("No Full night channels are enabled.");
    await Promise.all(styles);
    await Promise.all(Array.from(doc.images).map(img=>img.decode()));
    await doc.fonts.ready;
    await authorize();
    signal.throwIfAborted();
    frame.contentWindow!.addEventListener("afterprint",cleanup,{once:true});
    // Some browsers do not dispatch afterprint on cancellation. Bound retention.
    timer=setTimeout(cleanup,120_000);
    frame.contentWindow!.focus();frame.contentWindow!.print();
    return cleanup;
  } catch(error){cleanup();throw error;}
}
